import { session } from "@/lib/auth";
import { loginUrlFor } from "@/lib/return-to";
import { toast } from "@/stores/toastStore";
import type { BackendErrorBody, Envelope } from "@/types/api";
import axios, { type AxiosRequestConfig, type InternalAxiosRequestConfig } from "axios";
import { type ApiErrorInfo, knownErrorCodes, resolveApiError } from "./errors";

/** Per-request opt-outs. `silent` suppresses the automatic error toast. */
export interface JupilotRequestConfig extends AxiosRequestConfig {
  silent?: boolean;
}

type InternalConfig = InternalAxiosRequestConfig & { silent?: boolean };

/**
 * The error every screen sees. `status` is what `query-client.ts` inspects to
 * decide against retrying a 4xx, so it has to be a plain own property.
 */
export class ApiError extends Error {
  readonly code: string;
  readonly status: number | null;
  readonly action: ApiErrorInfo["action"];
  readonly requestId: string | null;
  /**
   * 서버가 보낸 원문 메시지.
   *
   * `message` 는 우리 사본(`resolveApiError`)이고, 이건 서버가 직접 준 문장이에요. 보통은
   * 사본이 낫습니다 — 프록시를 통해 올라온 토스 오류는 영문이고 내부 용어를 그대로 노출해요.
   * 다만 **우리 api 자신이 낸 오류**는 이미 한국어 완성 문장이라, 코드 매핑이 없는 경우
   * 상태코드 기본 문구보다 이쪽이 정확합니다. 화면이 필요할 때만 골라 쓰라고 함께 실어요.
   */
  readonly serverMessage: string | null;

  constructor(
    info: ApiErrorInfo,
    status: number | null,
    requestId: string | null,
    serverMessage: string | null = null
  ) {
    super(info.message);
    this.name = "ApiError";
    this.code = info.code;
    this.status = status;
    this.action = info.action;
    this.requestId = requestId;
    this.serverMessage = serverMessage;
  }
}

export const apiClient = axios.create({
  baseURL: import.meta.env.VITE_API_URL,
  timeout: 20_000,
  headers: { "Content-Type": "application/json" },
});

apiClient.interceptors.request.use((config) => {
  // Our own session JWT. There is no refresh token — the server sets `exp` and the
  // operator logs in again. An already-expired token would only burn a round trip,
  // so `getToken` drops it instead of attaching it.
  const token = session.getToken();
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

apiClient.interceptors.response.use(
  (response) => response,
  (error: unknown) => {
    const { info, status, requestId, silent, url, serverMessage } = describe(error);
    if (status === 401 && !isLoginRequest(url)) {
      // 서버가 세션을 거부했으면 들고 있어 봐야 매 요청이 401 이 돼요. 토큰을 버리고
      // 로그인으로 보냅니다. 이게 없던 동안에는 만료된 토큰으로 화면이 그려지다가
      // 위젯마다 실패 토스트가 쏟아졌어요.
      session.clear();
      if (typeof window !== "undefined" && window.location.pathname !== "/login") {
        // 보던 곳을 `?next=` 로 기억해요 — MCP 동의 화면(`/connect?request=…`)은 그 쿼리를 잃으면
        // 요청이 끊겨서 AI 도구에서 처음부터 다시 해야 해요.
        window.location.replace(
          loginUrlFor(`${window.location.pathname}${window.location.search}`)
        );
      }
    }
    // 401 은 로그인 화면으로 보내는 것으로 이미 설명돼요. 토스트는 소음이에요.
    if (!silent && status !== 401) toast.error(info.message);
    return Promise.reject(new ApiError(info, status, requestId, serverMessage));
  }
);

/** 로그인 요청 자체의 401(비밀번호 오류)은 리다이렉트 대상이 아니에요 — 폼이 사유를 보여줘야 해요. */
function isLoginRequest(url: string | null): boolean {
  return url?.includes("/auth/login") === true;
}

function describe(error: unknown): {
  info: ApiErrorInfo;
  status: number | null;
  requestId: string | null;
  silent: boolean;
  url: string | null;
  serverMessage: string | null;
} {
  if (!axios.isAxiosError(error)) {
    return {
      info: resolveApiError(null, null),
      status: null,
      requestId: null,
      silent: false,
      url: null,
      serverMessage: null,
    };
  }
  const status = error.response?.status ?? null;
  const body = error.response?.data as BackendErrorBody | undefined;
  const code = body?.error?.code ?? null;
  const raw = typeof body?.error?.message === "string" ? body.error.message : null;
  return {
    info: resolveApiError(code, status, raw),
    status,
    requestId: body?.request_id ?? error.response?.headers?.["x-request-id"] ?? null,
    silent: (error.config as InternalConfig | undefined)?.silent === true,
    url: error.config?.url ?? null,
    serverMessage: raw,
  };
}

/** Unwrap `{data, request_id}`; tolerate a bare payload for `204`-ish routes. */
function unwrap<T>(payload: Envelope<T> | T): T {
  if (payload && typeof payload === "object" && "data" in (payload as Envelope<T>)) {
    return (payload as Envelope<T>).data;
  }
  return payload as T;
}

export const api = {
  async get<T>(url: string, config?: JupilotRequestConfig): Promise<T> {
    const res = await apiClient.get<Envelope<T>>(url, config);
    return unwrap(res.data);
  },
  async post<T>(url: string, body?: unknown, config?: JupilotRequestConfig): Promise<T> {
    const res = await apiClient.post<Envelope<T>>(url, body, config);
    return unwrap(res.data);
  },
  async put<T>(url: string, body?: unknown, config?: JupilotRequestConfig): Promise<T> {
    const res = await apiClient.put<Envelope<T>>(url, body, config);
    return unwrap(res.data);
  },
  async patch<T>(url: string, body?: unknown, config?: JupilotRequestConfig): Promise<T> {
    const res = await apiClient.patch<Envelope<T>>(url, body, config);
    return unwrap(res.data);
  },
  async delete<T>(url: string, config?: JupilotRequestConfig): Promise<T> {
    const res = await apiClient.delete<Envelope<T>>(url, config);
    return unwrap(res.data);
  },
};

/** The Korean sentence for any thrown value — pages never print `error.message` blind. */
export function errorMessage(error: unknown): string {
  if (error instanceof ApiError) return error.message;
  return resolveApiError(null, null).message;
}

export function errorAction(error: unknown): ApiErrorInfo["action"] {
  return error instanceof ApiError ? error.action : undefined;
}

/**
 * 서버가 쓴 한국어 문장을 우선하는 변형. 코드 사본이 있으면 그쪽이 이겨요.
 *
 * 우리 api 는 미지원 기능·검증 실패를 **완성된 한국어 422** 로 돌려줍니다
 * (예: "오버레이 기간은 1 이상의 정수여야 해요: sma:0"). 그런데 그 코드는 매핑
 * 테이블에 없으니 `errorMessage` 는 상태코드 기본 문구 "입력값을 확인해 주세요." 로
 * 덮어써요 — 서버가 무엇이 틀렸는지 정확히 말해 준 걸 화면이 지우는 셈입니다.
 * 반대로 매핑이 있는 코드(토스발 오류)는 서버 원문이 영문·내부 용어라 사본이 낫습니다.
 */
export function serverErrorMessage(error: unknown): string {
  if (
    error instanceof ApiError &&
    error.serverMessage?.trim() &&
    !knownErrorCodes().includes(error.code)
  ) {
    return error.serverMessage;
  }
  return errorMessage(error);
}
